import React, { useCallback, useEffect, useState } from "react";

import { useHistory, useParams, useRouteMatch } from "react-router-dom";

import CompanyEdit from "../../../../../../presentation/pages/admin/companies/edit";
import NfseNacionalCard from "../../../../../../presentation/components/nfse-nacional-card";

import {
  addressTypeList,
  especialTaxTypeList,
  taxTypeList,
} from "../../../../../../utils";

import {
  handleCepChange,
  handleNoStackChange,
  getErrorMessage,
  makeDeveloper,
  makeCompany,
  showErrorMessage,
  showSuccessMessage,
  timeout,
  handleCnpjChange,
} from "../../../../../../presentation/helpers";

import { makeRemoteStack } from "../../../../usecases/stack/remote-stack-factory";
import { makeRemoteCompanies } from "../../../../usecases/companies/remote-companies-factory";
import { makeRemoteDeveloper } from "../../../../usecases/developer/remote-developer-factory";
import { makeRemoteSolicitation } from "../../../../usecases/solicitation/remote-solicitation-factory";
import { makeRemoteDeveloperCompanies } from "../../../../usecases/developer-companies/remote-developer-companies-factory";

import { DeveloperProps } from "../../../../../../domain/models/developer.model";
import { ISolicitation } from "../../../../../../presentation/interfaces";
import { applyNamedMask } from "../../../../../../utils/mask";

const MakeCompanyEdit: React.FC = () => {
  const isSmallScreen = window.innerWidth < 768;

  const match = useRouteMatch<{ action: string }>([
    "/admin/developers/edit/:id",
    "/admin/developers/view/:id",
  ]);
  const isViewPage = match?.path.includes("view");

  const history = useHistory();

  const { id } = useParams<{
    id: string;
  }>();

  const [errorMessage, setErrorMessage] = useState<string | null>("");

  const [stacks, setStacks] = useState<any[]>([]);

  const [developerCompany, setDeveloperCompany] = useState<DeveloperProps>();

  const [solicitation, setSolicitation] = useState<ISolicitation>();

  useEffect(() => {
    const fetchStacks = async (): Promise<void> => {
      const response = await makeRemoteStack().loadAll();
      setStacks(response);
    };
    fetchStacks();
  }, []);

  useEffect(() => {
    const fetchDeveloperCompany = async (): Promise<void> => {
      const response = await makeRemoteDeveloper().loadById(Number(id));
      setDeveloperCompany(response);
    };

    fetchDeveloperCompany();
  }, [id]);

  useEffect(() => {
    const fetchSolicitation = async (): Promise<void> => {
      const response = await makeRemoteSolicitation().loadAll();

      const findedSolicitation = response.find(
        (solic: any) => solic.developer?.id === Number(id),
      );

      setSolicitation(findedSolicitation as any);
    };

    fetchSolicitation();
  }, [id]);

  const createDeveloper = (data: any): any => ({
    id: developerCompany?.id,
    ...makeDeveloper(data),
    stacks: (data.stacks && data.stacks.length > 0)
      ? data.stacks.map((stack: any) => ({ id: String(stack) }))
      : developerCompany?.stacks?.map((stack) => ({ id: String(stack.id) })) || [],
  });

  const createCompany = (data: any): any => ({
    id: developerCompany?.company?.id,
    ...makeCompany(data),
  });

  const createRequestBody = (data: any): any => ({
    developer: createDeveloper(data),
    company: createCompany(data),
  });

  const updateDeveloper = async (developerData: any): Promise<void> => {
    await makeRemoteDeveloper().update(
      developerCompany?.id || 0,
      developerData as any,
    );
  };

  const updateCompany = async (companyData: any): Promise<void> => {
    await makeRemoteCompanies().update(
      developerCompany?.company?.id || 0,
      companyData as any,
    );
  };

  const createAndLinkCompany = async (companyData: any): Promise<void> => {
    const createdCompany = await makeRemoteCompanies().create(
      companyData as any,
    );

    const createdDeveloperCompany = await makeRemoteDeveloperCompanies().create(
      {
        company: { id: createdCompany.id },
        developer: { id: solicitation?.developer?.id },
        solicitation: { id: solicitation?.id as number },
      },
    );

    setSolicitation({
      ...solicitation,
      developerCompany: createdDeveloperCompany,
    } as ISolicitation);
  };

  const isUpdatingDeveloper = (): boolean => Boolean(developerCompany?.id);

  const isUpdatingCompany = (): boolean => Boolean(developerCompany?.company);

  const updateDeveloperAndCompany = async (requestBody: any): Promise<void> => {
    if (isUpdatingDeveloper()) {
      await updateDeveloper(requestBody.developer);
    }

    if (isUpdatingCompany()) {
      await updateCompany(requestBody.company);
    } else {
      await createAndLinkCompany(requestBody.company);
    }
  };

  const handleError = (error: any): void => {
    const errorMsg = getErrorMessage(error);

    setErrorMessage(errorMsg);
    showErrorMessage(errorMsg);
  };

  const handleSubmit = async (data: any): Promise<void> => {
    const requestBody = createRequestBody(data);

    try {
      await updateDeveloperAndCompany(requestBody);

      showSuccessMessage("Dados atualizados com sucesso!");

      timeout(history.goBack, 2);
    } catch (error: any) {
      handleError(error);
    }
  };

  const goBack = (): void => {
    history.goBack();
  };

  const NfseNacionalComponent = useCallback(
    () => (
      <NfseNacionalCard
        mode="array"
        initialChecked={developerCompany?.company?.nfseNacional}
        disabled={isViewPage}
      />
    ),
    [developerCompany?.company?.nfseNacional, isViewPage],
  );

  const dataForm = [
    {
      items: [
        {
          name: "title",
          component: "Title",
          title: "Dados pessoais: ",
          number: 1,
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "2fr 1fr" },
          inputs: [
            {
              name: "name",
              component: "Input",
              value: developerCompany?.name || "",
              title: "Nome",
              label: "Nome",
              disabled: isViewPage,
            },
            {
              component: "InputMask",
              mask: "CPF",
              name: "cpf",
              label: "CPF",
              title: "CPF",
              value: developerCompany?.cpf || "",
              validations: "cpfValidationNotRequired",
              disabled: isViewPage,
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: isSmallScreen ? "1fr 1fr" : "1fr 1fr 1fr",
          },
          inputs: [
            {
              name: "email",
              component: "Input",
              value: developerCompany?.email || "",
              title: "E-mail",
              label: "E-mail",
              disabled: isViewPage,
              validations: "validEmail",
            },
            {
              component: "InputMask",
              mask: "PHONE_NUMBER",
              name: "phone",
              label: "Telefone",
              title: "Telefone",
              value: `${developerCompany?.phone?.ddd}${developerCompany?.phone?.number}`,
              disabled: isViewPage,
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: isSmallScreen ? "1fr 1fr" : "2fr 1fr",
          },
          hasDivider: true,
          inputs: [
            {
              name: "stacks",
              title: "Stacks",
              component: "Multiselect",
              disabled: isViewPage,
              styleContainer: { margin: "0px" },
              initialSelectedOptions:
                developerCompany?.stacks?.map((stack) => stack.id) || [],
              options: stacks
                .filter((stack) => stack.description !== "Não desejo informar")
                .map((stack) => ({
                  label: stack.description,
                  value: stack.id,
                })),
            },
            {
              component: "InputGroup",
              style: { display: "flex", flexDirection: "column" },
              inputs: [
                {
                  name: "otherStack",
                  component: "MultipleTextInput",
                  styleContainer: { margin: "0px" },
                  value: developerCompany?.otherStack || "",
                  title: "Outra stack",
                  label: "Outra stack",
                  disabled: isViewPage,
                },
                {
                  name: "noStack",
                  component: "InputCheck",
                  title: "",
                  label: "",
                  onChange: (value: any, el: any) =>
                    handleNoStackChange(value, el, stacks),
                  checked:
                    developerCompany?.stacks?.some(
                      (stack) => stack.description === "Não desejo informar",
                    ) || false,
                  options: [
                    {
                      value: "true",
                      label: "Não desejo informar",
                      name: "Não desejo informar",
                    },
                  ],
                  disabled: isViewPage,
                },
              ],
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Endereço Residencial: ",
          number: 2,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
          },
          inputs: [
            {
              name: "developerCep",
              component: "InputMask",
              mask: "CEP",
              label: "CEP",
              title: "CEP",
              value: developerCompany?.address?.zipCode || "",
              onChange: (data: any, el: any) =>
                handleCepChange(data, el, "developer"),
              disabled: isViewPage,
            },
            {
              component: "Input",
              name: "developerStreet",
              label: "Rua",
              title: "Rua",
              value: developerCompany?.address?.place || "",
              disabled: isViewPage,
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "developerNumber",
              component: "Input",
              label: "Número",
              title: "Número",
              value: developerCompany?.address?.number || "",
              disabled: isViewPage,
            },
            {
              name: "developerNeighborhood",
              component: "Input",
              label: "Bairro",
              title: "Bairro",
              value: developerCompany?.address?.district || "",
              disabled: isViewPage,
            },
            {
              name: "developerComplement",
              component: "Input",
              label: "Complemento",
              title: "Complemento",
              value: developerCompany?.address?.complement || "",
              disabled: isViewPage,
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          hasDivider: true,
          inputs: [
            {
              name: "developerCity",
              component: "Input",
              label: "Cidade",
              title: "Cidade",
              value: developerCompany?.address?.city || "",
              disabled: isViewPage,
            },
            {
              name: "developerState",
              component: "Input",
              label: "Estado",
              title: "Estado",
              value: developerCompany?.address?.state || "",
              disabled: isViewPage,
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Dados CNPJ: ",
          number: 3,
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "2fr 1fr" },
          inputs: [
            {
              name: "socialReason",
              component: "Input",
              label: "Razão Social",
              title: "Razão Social",
              value: developerCompany?.company?.socialReason || "",
              disabled: isViewPage,
            },
            {
              component: "InputMask",
              mask: "CNPJ",
              name: "cnpj",
              label: "CNPJ",
              title: "CNPJ",
              value: developerCompany?.company?.subscriptionNumber || "",
              onChange: (data: any, el: any) =>
                handleCnpjChange(data, el),
              validations: "cnpjValidationNotRequired",
              disabled: isViewPage,
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            alignItems: "center",
          },
          inputs: [
            {
              name: "stateRegistration",
              component: "Input",
              label: "Inscrição Estadual",
              title: "Inscrição Estadual",
              value: developerCompany?.company?.stateRegistration || "",
              disabled: isViewPage,
            },
            {
              name: "municipalRegistration",
              component: "Input",
              label: "Inscrição Municipal",
              title: "Inscrição Municipal",
              value: developerCompany?.company?.municipalRegistration || "",
              disabled: isViewPage,
            },
            {
              name: "isMei",
              component: "InputCheck",
              disabled: isViewPage,
              title: "",
              label: "",
              checked: developerCompany?.company?.isMei || false,
              options: [
                {
                  value: "true",
                  label: "O CNPJ é MEI?",
                  name: "O CNPJ é MEI?",
                },
              ],
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "flex",
            width: "100%",
          },
          inputs: [
            {
              name: "companyActivities",
              component: "ActivitiesList",
              label: "Atividades Econômicas (CNAE)",
              title: "Atividades Econômicas (CNAE)",
              value: [],
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "2fr 1fr" },
          inputs: [
            {
              name: "fantasyName",
              component: "Input",
              label: "Nome Fantasia",
              title: "Nome Fantasia",
              value: developerCompany?.company?.fantasyName || "",
              disabled: isViewPage,
            },
            {
              component: "Input",
              name: "companyEmail",
              label: "E-mail Cadastrado",
              title: "E-mail Cadastrado",
              value: developerCompany?.company?.email || "",
              disabled: isViewPage,
              validations: "validEmail",
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "companyPhone",
              component: "InputMask",
              label: "Telefone",
              mask: "PHONE_NUMBER",
              title: "Telefone",
              placeholder: "(00) 0000-0000",
              value: (developerCompany?.company?.phone?.ddd &&
                developerCompany?.company?.phone?.number) ? applyNamedMask(
                  `${developerCompany?.company?.phone?.ddd}${developerCompany?.company?.phone?.number}`, "PHONE_NUMBER") :
                ""
              ,
              disabled: isViewPage,
            },
            {
              component: "InputMask",
              mask: "MOBILE_NUMBER",
              name: "companyMobilePhone",
              placeholder: "(00) 00000-0000",
              label: "Celular",
              title: "Celular",
              value: (developerCompany?.company?.cellPhone?.ddd &&
                developerCompany?.company?.cellPhone?.number) ? applyNamedMask(
                  `${developerCompany?.company?.cellPhone?.ddd}${developerCompany?.company?.cellPhone?.number}`, "MOBILE_NUMBER") :
                ""
              ,
              disabled: isViewPage,
            },
            {
              name: "taxType",
              component: "Select",
              label: "Regime Tributário",
              title: "Regime Tributário",
              value: developerCompany?.company?.taxType || "",
              onChange: (value: string, setFieldValue: any) => {
                if (setFieldValue && (value === "SIMPLES_NACIONAL" || value === "SIMPLES_NACIONAL_EXCESSO")) {
                  setFieldValue("simplesNacional", ["true"]);
                }
              },
              options: taxTypeList.map((tax) => ({
                value: tax.ENUM,
                label: tax.label,
              })),
              disabled: isViewPage,
            },
            {
              name: "especialTaxType",
              component: "Select",
              label: "Regime Tributário Especial",
              title: "Regime Tributário Especial",
              value: developerCompany?.company?.especialTaxType || especialTaxTypeList.find((item) => item.value === 9)?.ENUM,
              options: especialTaxTypeList.map((tax) => ({
                value: tax.ENUM,
                label: tax.label,
              })),
              disabled: isViewPage,
            },
          ],
        },
        {
          component: "InputGroup",
          hasDivider: true,
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "simplesNacional",
              component: "InputCheck",
              title: "",
              label: "",
              checked: developerCompany?.company?.simpleNacional || false,
              options: [
                {
                  value: "true",
                  label: "Optante pelo Simples Nacional",
                  name: "Optante pelo Simples Nacional",
                },
              ],
              disabled: isViewPage,
            },
            {
              name: "culturalIncentive",
              component: "InputCheck",
              title: "",
              label: "",
              checked: developerCompany?.company?.culturalIncentive || false,
              options: [
                {
                  value: "true",
                  label: "Incentivo a Cultura",
                  name: "Incentivo a Cultura",
                },
              ],
              disabled: isViewPage,
            },
            {
              name: "taxIncentive",
              component: "InputCheck",
              title: "",
              label: "",
              checked: developerCompany?.company?.taxIncentive || false,
              options: [
                {
                  value: "true",
                  label: "Taxa de incentivo",
                  name: "Taxa de incentivo",
                },
              ],
              disabled: isViewPage,
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Nota Fiscal Nacional: ",
          number: 4,
        },
        {
          component: "InputGroup",
          hasDivider: true,
          style: { display: "grid", gridTemplateColumns: "1fr" },
          inputs: [
            {
              name: "nfseNacional",
              component: NfseNacionalComponent,
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Prefeitura: ",
          number: 5,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
          },
          hasDivider: true,
          inputs: [
            {
              name: "cityHallLogin",
              component: "Input",
              label: "Login da Prefeitura",
              title: "Login da Prefeitura",
              value: developerCompany?.company?.cityHall?.login || "",
              disabled: isViewPage,
            },
            {
              name: "cityHallPassword",
              component: "InputPassword",
              type: "password",
              label: "Senha da Prefeitura",
              title: "Senha da Prefeitura",
              value: developerCompany?.company?.cityHall?.password || "",
              disabled: isViewPage,
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Endereço do CNPJ: ",
          number: 6,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
          },
          inputs: [
            {
              name: "companyCep",
              component: "InputMask",
              mask: "CEP",
              label: "CEP",
              title: "CEP",
              value: developerCompany?.company?.address?.zipCode || "",
              onChange: (data: any, el: any) =>
                handleCepChange(data, el, "company"),
              disabled: isViewPage,
            },
            {
              name: "tipoLogradouro",
              component: "Select",
              label: "Tipo de Logradouro",
              title: "Tipo de Logradouro",
              value:
                solicitation?.developerCompany?.company?.address
                  ?.tipoLogradouro || "",
              required: true,
              validations: "inputRequired",
              options: addressTypeList.map((address) => ({
                value: address.ENUM,
                label: address.label,
              })),
              disabled: isViewPage,
            },
            {
              component: "Input",
              name: "companyStreet",
              label: "Rua",
              title: "Rua",
              value: developerCompany?.company?.address?.place || "",
              disabled: isViewPage,
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "companyNumber",
              component: "Input",
              label: "Número",
              title: "Número",
              value: developerCompany?.company?.address?.number || "",
              disabled: isViewPage,
            },
            {
              name: "companyNeighborhood",
              component: "Input",
              label: "Bairro",
              title: "Bairro",
              value: developerCompany?.company?.address?.district || "",
              disabled: isViewPage,
            },
            {
              name: "companyComplement",
              component: "Input",
              label: "Complemento",
              title: "Complemento",
              value: developerCompany?.company?.address?.complement || "",
              disabled: isViewPage,
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          hasDivider: true,
          inputs: [
            {
              name: "companyCity",
              component: "Input",
              label: "Cidade",
              title: "Cidade",
              value: developerCompany?.company?.address?.city || "",
              disabled: isViewPage,
            },
            {
              name: "companyState",
              component: "Input",
              label: "Estado",
              title: "Estado",
              value: developerCompany?.company?.address?.state || "",
              disabled: isViewPage,
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "flex", gap: "16px", width: "100%", marginTop: 16 },
          inputs: [
            {
              name: "backButton",
              component: "Button",
              type: "button",
              title: "Voltar",
              onClick: goBack,
              color: false,
            },
            {
              name: "button",
              component: "Button",
              title: "Enviar",
              submittingText: "Carregando...",
              color: true,
              disabled: isViewPage,
            },
          ],
        },
      ],
    },
  ];

  const props = {
    dataForm,
    onSubmit: handleSubmit,
    errorMessage,
  };

  return <CompanyEdit {...props} />;
};

export default MakeCompanyEdit;
